// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import {
  Button,
  ColumnsContainer,
  GSGrid,
  SectionComponent,
  View,
  Text,
  ModalBody,
  ModalFooter,
  Modal,
} from "@components";
import { compose } from "redux";
import type { State } from "@store/reducers";
import type {
  RtwInterviewForm,
  RtwInterviewFormQuestionAnswer,
  EmployeeAbsence,
} from "@modules/shared/reducer/employee-absences/types";
import { formatDateTime, formatDate } from "@transformers/date";
import {
  getRtwInterviewForm,
  getAbsenceDetails,
  openRtwInterviewModal,
} from "@modules/shared/selectors/employee-absences";
import { formatQuestionAnswerResponse } from "@modules/shared/utils/question-answer-response";
import { differenceInHours } from "date-fns";
import { RTW_INTERVIEW_STATUSES } from "@modules/shared/constants/employee-absences";
import classNames from "classnames";
import AbsenceRateTrendRTWInterviewModal from "./rtw-interview-modal";
import type { SelectedTask } from "@modules/reporting/reducer/tasks/types";
import { getSelectedTask, getSelectedTaskRequestCount } from "@modules/reporting/selectors/tasks";
import reportingActionCreators from "@modules/reporting/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import {
  RTWEmployeeDetailsView,
  RTWInterviewForm,
  RTWTaskDetailsViewSection,
  RTWAbsenceDetailsView,
} from "@modules/reporting/components";
import { hasPermission } from "@modules/account/selectors";
import { PermissionEnum } from "@modules/account/components";

type OwnProps = $Exact<{
  id: string,
  clientId: number,
  employeeId: number,
  absenceId: number,
  isReportingSection?: boolean,
}>;

type PropsFromState = $Exact<{
  data: ?RtwInterviewForm,
  absence: EmployeeAbsence | null,
  openRtwInterviewModal: boolean,
  selectedTask: ?SelectedTask,
  taskRequestCount: number,
  shouldCancelRtwTask: boolean,
  shouldDeleteRtwTask: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getEmployeeAbsence: typeof sharedActionCreators.GetEmployeeAbsence,
  setVisibleRTWInterviewModal: typeof sharedActionCreators.SetVisibleAbsenceRateTrendRTWInterviewModal,
  selectRtwTask: typeof reportingActionCreators.GetRtwInterviewTask,
  cancelRtwInterview: typeof sharedActionCreators.CancelRtwInterview,
  deleteRtwInterview: typeof sharedActionCreators.DeleteRtwInterview,
  rebuildRtwInterview: typeof sharedActionCreators.RebuildRtwInterview,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type ComponentState = $Exact<{
  isSectionOpen: boolean,
  isDeleteRtwInterviewModalOpen: boolean,
}>;

export class AbsenceDetailsRTWInterview extends React.Component<Props, ComponentState> {
  state = {
    isSectionOpen: true,
    isDeleteRtwInterviewModalOpen: false,
  };

  componentDidUpdate(prevProps: Object) {
    if (
      this.props.data &&
      prevProps.data !== this.props.data &&
      this.props.taskRequestCount === 0
    ) {
      this.props.selectRtwTask({
        clientId: this.props.clientId,
        taskId: (this.props.data || {}).id,
      });
    }
  }

  get interviewDetailsColumns() {
    const { t } = this.props;
    const data = this.props.data || {};
    const dueDate = data.dueDate ? formatDateTime(data.dueDate) : t("N/A");
    const completionDate = data.completionDate ? formatDateTime(data.completionDate) : t("N/A");
    const completedBy = data.completedBy ? data.completedBy.name : t("N/A");
    const updatedAt = data.updatedAt ? formatDateTime(data.updatedAt) : t("N/A");

    return [
      {
        columns: [
          {
            data: [[t("DATE_INTERVIEW_DUE"), dueDate]],
          },
          {
            data: [[t("DATE_COMPLETED"), completionDate]],
          },
          {
            data: [[t("COMPLETED_BY"), completedBy]],
          },
          {
            data: [[t("TIME_TO_COMPLETE"), this.timeToCompleteInterview]],
          },
          {
            data: [[t("Status"), this.statusMsg]],
          },
          {
            data: [[t("DATE_SUBMITTED"), updatedAt]],
          },
        ],
      },
    ];
  }

  get questionsColumns() {
    return [
      {
        key: "id",
        name: this.props.t("ID"),
        renderCell: ({ row }: { row: RtwInterviewFormQuestionAnswer }) => (
          <View alignSelf="start" padding={[1, 0, 0, 0]}>
            {row?.question?.id}
          </View>
        ),
        width: "min-content",
      },
      {
        key: "question",
        name: this.props.t("QUESTION"),
        renderCell: ({ row }: { row: RtwInterviewFormQuestionAnswer }) => (
          <textarea
            disabled
            rows="3"
            maxLength="600"
            className="no-resize background-color-transparent borders-solid-color-white fs-14 line-height-20"
            defaultValue={row.question?.question}
          />
        ),
      },
      {
        key: "employeeResponse",
        name: this.props.t("ANSWER"),
        renderCell: ({ row }: { row: RtwInterviewFormQuestionAnswer }) =>
          formatQuestionAnswerResponse(row.answer, this.props.t),
        align: "left",
      },
    ];
  }

  get hasFormExpired() {
    return this.props.data && this.props.data.hasExpired;
  }

  get timeToCompleteInterview(): string {
    const { rtwAt } = this.props.absence || {};
    const { completionDate } = this.props.data || {};

    if (!completionDate || !rtwAt) {
      return this.props.t("N/A");
    }

    return `${differenceInHours(completionDate, rtwAt)} Hours`;
  }

  get statusMsg() {
    const { complianceStatus } = this.props.data || {};

    return (
      <span
        className={classNames("font-weight-bold", {
          "text-negative":
            complianceStatus === RTW_INTERVIEW_STATUSES.EXPIRED ||
            complianceStatus === RTW_INTERVIEW_STATUSES.COMPLETED_LATE,
          "text-light-green": complianceStatus === RTW_INTERVIEW_STATUSES.COMPLIANT,
          "text-pumpkin-orange":
            complianceStatus === RTW_INTERVIEW_STATUSES.OUTSTANDING ||
            complianceStatus === RTW_INTERVIEW_STATUSES.OVERDUE,
        })}
      >
        {this.props.t(complianceStatus)}
      </span>
    );
  }

  get completeInterviewFormButton() {
    const { complianceStatus } = this.props.data || {};

    if (
      complianceStatus === RTW_INTERVIEW_STATUSES.OUTSTANDING ||
      complianceStatus === RTW_INTERVIEW_STATUSES.OVERDUE
    ) {
      return (
        <div className="d-flex justify-content-center mt-3">
          {!this.props.isReportingSection && this.props.shouldCancelRtwTask && (
            <Button size="small" color="negative" onClick={this.cancelRTW} className="mr-3">
              {this.props.t("CANCEL_RTW_FORM")}
            </Button>
          )}

          <Button size="small" onClick={this.completeRTW}>
            {this.props.t("COMPLETE_RTW_FORM")}
          </Button>
        </div>
      );
    }
    return null;
  }

  cancelRTW = () => {
    const questionAnswers =
      this.props.selectedTask &&
      this.props.selectedTask.questionAnswers &&
      this.props.selectedTask.questionAnswers.filter(
        item =>
          this.props.data &&
          this.props.data.questionAnswers.find(
            i => i.question && i.question.id === item.question.id,
          ),
      );

    this.props.cancelRtwInterview({
      clientId: this.props.clientId,
      task: {
        ...this.props.selectedTask,
        questionAnswers,
        status: "CANCELLED",
      },
    });
  };

  completeRTW = () => {
    this.props.setVisibleRTWInterviewModal(true);
  };

  onCloseHandler = () => {
    this.props.setVisibleRTWInterviewModal(false);

    this.props.getEmployeeAbsence({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      absenceId: this.props.absenceId,
      isReportingRequest: true,
    });
  };

  printForm = () => {
    const body = document.body;
    const bodyChildren = body.children;

    const print_form = document.createElement("div");
    if (!body || !print_form) return false;
    print_form.id = "temporary-content";
    print_form.innerHTML = document.getElementById("print_form")?.innerHTML || "";
    //$FlowIgnore
    for (let prop in bodyChildren) bodyChildren[prop]?.classList?.add("d-none");
    body.appendChild(print_form);
    window.print();
    //$FlowIgnore
    for (let prop in bodyChildren) bodyChildren[prop]?.classList?.remove("d-none");
    body.removeChild(print_form);
  };

  get rtwForm() {
    return (
      <div className="d-none" id="print_form">
        <div className="section mb-0">
          <h4 className="font-weight-bold py-3">{this.props.t("TASK_DETAILS")}</h4>
          {this.props.selectedTask && (
            <RTWTaskDetailsViewSection selectedTask={this.props.selectedTask} />
          )}
        </div>
        <div className="section mb-0">
          <div className="section mb-0">
            <h4 className="font-weight-bold py-3">{this.props.t("EMPLOYEE_DETAILS")}</h4>
            {this.props.selectedTask && (
              <RTWEmployeeDetailsView selectedTask={this.props.selectedTask} />
            )}
          </div>
        </div>
        <div className="section mb-0">
          <div className="section mb-0">
            <h4 className="font-weight-bold py-3">{this.props.t("ABSENCE_DETAILS")}</h4>
            {this.props.selectedTask && (
              <RTWAbsenceDetailsView selectedTask={this.props.selectedTask} />
            )}
          </div>
        </div>
        <div className="section">
          <h4 className="mb-3 font-weight-bold">{this.props.t("RTW_INTERVIEW")}</h4>
          <RTWInterviewForm clientId={this.props.clientId} printForm={false} visible={true} />
        </div>
      </div>
    );
  }

  get data() {
    if (!this.props.data) {
      return [];
    }

    return this.props.data.questionAnswers.sort((a, b) => a.question.id - b.question.id);
  }

  get hasCancelled() {
    const { complianceStatus } = this.props.data || {};
    return complianceStatus === RTW_INTERVIEW_STATUSES.CANCELLED;
  }

  toggleSection = () => {
    this.setState({
      isSectionOpen: !this.state.isSectionOpen,
    });
  };

  toggleOpenDeleteRtwInterviewModal = (open: boolean) => () => {
    this.setState({ isDeleteRtwInterviewModalOpen: open });
  };

  onDeleteRTW = () => {
    this.toggleOpenDeleteRtwInterviewModal(false)();

    this.props.deleteRtwInterview({
      clientId: this.props.clientId,
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      absenceId: this.props.absenceId,
      task: this.props.selectedTask,
    });
  };

  onRebuildRTW = () => {
    this.toggleOpenDeleteRtwInterviewModal(false)();

    this.props.rebuildRtwInterview({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      absenceId: this.props.absenceId,
      task: this.props.selectedTask,
    });
  };

  render() {
    const { data } = this.props || {};
    const { id } = data || {};

    return (
      <SectionComponent
        id={this.props.id}
        wrapperClassName="flex-column flex-nowrap"
        title={<h5>{this.props.t("RTW_INTERVIEW")}</h5>}
        className="gs-container"
        rightLink={
          <View direction="row" gap={2}>
            {this.props.shouldDeleteRtwTask && (
              <Button
                className="ml-2 negative"
                onClick={this.toggleOpenDeleteRtwInterviewModal(true)}
              >
                {this.props.t("Delete")}
              </Button>
            )}

            {this.props.data && this.props.data.questionAnswers && !this.hasFormExpired && (
              <Button onClick={this.printForm}>{this.props.t("PRINT_FORM")}</Button>
            )}
          </View>
        }
        collapsable
        t={this.props.t}
        onTitleClick={this.toggleSection}
        isOpen={this.state.isSectionOpen}
      >
        <div>
          {data ? (
            <View gap={3}>
              <ColumnsContainer rows={this.interviewDetailsColumns} />
              {this.props.selectedTask && this.rtwForm}
              {data.questionAnswers.length && !this.hasFormExpired && !this.hasCancelled ? (
                <View>
                  <h5 className="pb-3">{data.rtwInterviewProcessName}</h5>
                  <GSGrid
                    rows={this.data}
                    rowKeyGetter={row => row.question.id}
                    columns={this.questionsColumns}
                    rowHeight={row => {
                      return ["FREE_TEXT", "SINGLE_SELECT"].includes(row.question.questionType)
                        ? 95
                        : 40;
                    }}
                  />
                </View>
              ) : null}
              {this.hasFormExpired ? (
                <View direction="row" justifyContent="center">
                  <Text type="h6">
                    {this.props.t("RTW_EXPIRY_TEXT", { date: formatDate(data.expiryDate) })}
                  </Text>
                </View>
              ) : null}
              {this.completeInterviewFormButton}
            </View>
          ) : null}
        </div>

        {this.props.openRtwInterviewModal && (
          <AbsenceRateTrendRTWInterviewModal
            clientId={this.props.clientId}
            rtwInterviewId={id}
            onClose={this.onCloseHandler}
            printForm={false}
          />
        )}

        {this.state.isDeleteRtwInterviewModalOpen && (
          <Modal className="modal-normal">
            <ModalBody>
              <h6>{this.props.t("DELETE_ABSENCE_RTW_INTERVIEW_CONFIRMATION_MESSAGE")}</h6>
            </ModalBody>
            <ModalFooter className="d-flex justify-content-end">
              <Button onClick={this.toggleOpenDeleteRtwInterviewModal(false)}>
                {this.props.t("KEEP_INTERVIEW_BTN_TEXT")}
              </Button>
              <Button className="ml-2 pumpkin-orange" onClick={this.onRebuildRTW}>
                {this.props.t("DELETE_CREATE_INTERVIEW_BTN_TEXT")}
              </Button>
              <Button className="ml-2 negative" onClick={this.onDeleteRTW}>
                {this.props.t("DELETE_INTERVIEW_BTN_TEXT")}
              </Button>
            </ModalFooter>
          </Modal>
        )}
      </SectionComponent>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => {
  const selectedTask = getSelectedTask(state);
  return {
    data: getRtwInterviewForm(state),
    absence: getAbsenceDetails(state),
    openRtwInterviewModal: openRtwInterviewModal(state),
    selectedTask,
    taskRequestCount: getSelectedTaskRequestCount(state),
    shouldCancelRtwTask: hasPermission(state, PermissionEnum.PRIVILEGE_TASKS_CANCEL),
    shouldDeleteRtwTask:
      !!selectedTask?.isDeletable && hasPermission(state, PermissionEnum.PRIVILEGE_TASKS_DELETE),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getEmployeeAbsence: sharedActionCreators.GetEmployeeAbsence,
  setVisibleRTWInterviewModal: sharedActionCreators.SetVisibleAbsenceRateTrendRTWInterviewModal,
  selectRtwTask: reportingActionCreators.GetRtwInterviewTask,
  cancelRtwInterview: sharedActionCreators.CancelRtwInterview,
  deleteRtwInterview: sharedActionCreators.DeleteRtwInterview,
  rebuildRtwInterview: sharedActionCreators.RebuildRtwInterview,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AbsenceDetailsRTWInterview);
